import { useEffect, useRef } from 'react'
import {
  DISPLAY_HEARTBEAT_MS,
  displayDeviceId,
  heartbeatBody,
  heartbeatReportKey,
  sendDisplayHeartbeat
} from '../lib/display-heartbeat.ts'
import {
  getDisplayClock,
  getDisplaySession,
  subscribeDisplay
} from '../lib/display-mode.ts'
import { reloadAfterBoot } from '../lib/display-reload.ts'

/** Send screen reports and process remote refresh requests for this page load. */
export function useDisplayHeartbeat(
  reportKey: string | null,
  reload: () => void
): void {
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])
  const refreshState = useRef<{
    bootServerTime: string | null
    reloadRequestedAt: string | null
    reloaded: boolean
  }>({ bootServerTime: null, reloadRequestedAt: null, reloaded: false })
  const enabled = reportKey !== null
  useEffect(() => {
    if (!enabled) {
      return undefined
    }
    // A device id that throws must not leave this effect. The rotation is
    // the next effect, and a heartbeat fault must not stop it.
    let deviceId: string
    let body: ReturnType<typeof heartbeatBody>
    try {
      deviceId = displayDeviceId()
      body = heartbeatBody(getDisplaySession(), deviceId)
    } catch {
      return undefined
    }
    if (body === null) {
      return undefined
    }

    const send = (
      report: NonNullable<ReturnType<typeof heartbeatBody>>
    ): void => {
      void sendDisplayHeartbeat(report).then(answer => {
        if (answer === null || !mounted.current) return
        const state = refreshState.current
        const seen = Date.parse(answer.lastSeenAt)
        if (
          Number.isFinite(seen) &&
          (state.bootServerTime === null ||
            seen < Date.parse(state.bootServerTime))
        ) {
          state.bootServerTime = answer.lastSeenAt
        }
        // An older answer can lower the boot time after a newer answer carried a request.
        if (
          answer.reloadRequestedAt !== null &&
          Number.isFinite(Date.parse(answer.reloadRequestedAt)) &&
          (state.reloadRequestedAt === null ||
            Date.parse(answer.reloadRequestedAt) >
              Date.parse(state.reloadRequestedAt))
        ) {
          state.reloadRequestedAt = answer.reloadRequestedAt
        }
        if (
          !state.reloaded &&
          reloadAfterBoot(state.bootServerTime, state.reloadRequestedAt)
        ) {
          state.reloaded = true
          reload()
        }
      })
    }
    const clock = getDisplayClock()
    let cancel = (): void => undefined
    let cancelExtra = (): void => undefined
    let lastExtra = -Infinity
    let previousKey = heartbeatReportKey(getDisplaySession())
    const currentBody = (): ReturnType<typeof heartbeatBody> =>
      heartbeatBody(getDisplaySession(), deviceId)
    const reportLatest = (): void => {
      try {
        const next = currentBody()
        if (next !== null) send(next)
      } catch {
        // A report failure must leave the next minute armed.
      }
    }
    const unsubscribe = subscribeDisplay(() => {
      try {
        const key = heartbeatReportKey(getDisplaySession())
        if (key === previousKey) return
        const next = currentBody()
        previousKey = key
        cancelExtra()
        if (next === null) return
        const wait = Math.max(0, 5000 - (clock.now() - lastExtra))
        const extra = (): void => {
          lastExtra = clock.now()
          reportLatest()
        }
        if (wait === 0) extra()
        else cancelExtra = clock.after(wait, extra)
      } catch {
        // A heartbeat failure must not escape into the display store.
      }
    })
    const arm = (): void => {
      cancel = clock.after(DISPLAY_HEARTBEAT_MS, () => {
        cancelExtra()
        reportLatest()
        arm()
      })
    }
    send(body)
    arm()

    return () => {
      unsubscribe()
      cancelExtra()
      cancel()
    }
  }, [enabled, reload])
}
